// Pure tick functions for the proposal-creation preview card. The preview
// renders <MarketRowCard> with simulated traffic so it reads as a live
// market: shares drift while always summing to 1, volume + prize-pool grow
// monotonically, and each tick produces a LiveTrade that the card's existing
// `<LiveTradesOverlay>` (spec 065) renders as a floating bubble.
//
// This module is React-free and side-effect-free — every function returns
// a new state and the caller owns timers + RNG. Tests inject a seeded RNG
// to make the invariants (sum-to-1, monotonic growth, share clamping)
// fully deterministic.

import type { LiveTrade } from '@/components/markets/live-trades/types';
import { D6_SCALE, D9_SCALE } from '@/config/solana';

export interface PreviewOutcomeState {
  /** Share in [0, 1]. All outcomes' shares sum to 1.0 within 1e-9. */
  share: number;
  /** D9-encoded TWAR value derived from `share`. */
  twarValueD9: string;
  /** D6-encoded reserves = share * volumeTotalD6. */
  reservesD6: string;
}

export interface PreviewSimState {
  volumeTotalD6: string;
  prizePoolD6: string;
  outcomes: ReadonlyArray<PreviewOutcomeState>;
  liveTrades: ReadonlyArray<LiveTrade>;
}

const INITIAL_VOLUME_USDC = 12_500;
const INITIAL_PRIZE_POOL_USDC = 5_000;
const HOUSE_TAKE_RATIO = 0.18;

// Trade-amount range. Log-distributed (small skew) so most trades sit in the
// lower half but the occasional whale lands a 4-figure clip — that's where
// the "more variation" feel comes from.
const MIN_TRADE_USDC = 25;
const MAX_TRADE_USDC = 1500;

// Tick cadence — jittered between MIN/MAX. Tightened from 700–2200 ms to
// 180–700 ms so the bubble overlay stays consistently busy and the volume +
// prize-pool footers visibly tick up every second or two.
const MIN_DELAY_MS = 180;
const MAX_DELAY_MS = 700;

// Per-tick share drift. Widened so individual trades visibly nudge the bars
// instead of looking glued. Still small enough that the leader doesn't flip
// every few ticks.
const SHARE_DRIFT_MAX = 0.028;
const SHARE_DRIFT_MIN = -0.02;
const SHARE_FLOOR = 0.01;
// Cap each outcome strictly below the canonical 66% consensus threshold so
// the preview never visually tips into "about to resolve". 0.55 leaves an
// 11 pp gap, which is wide enough that day-to-day drift can't bridge it.
const SHARE_CEILING = 0.55;

function uniformStartingShares(count: number): ReadonlyArray<number> {
  if (count <= 0) return [];
  if (count === 1) return [1];
  // Uniform 1/N — preview opens with every outcome at parity (50/50, 33/33/33,
  // …) and the simulation drifts from there.
  return Array.from({ length: count }, () => 1 / count);
}

function resolveStartingShares(
  outcomeCount: number,
  startingShares?: ReadonlyArray<number>,
): ReadonlyArray<number> {
  if (startingShares === undefined) {
    return uniformStartingShares(outcomeCount);
  }
  if (startingShares.length !== outcomeCount) {
    throw new Error(
      `startingShares length (${startingShares.length}) must match outcomeCount (${outcomeCount})`,
    );
  }
  const sum = startingShares.reduce((acc, share) => acc + share, 0);
  if (Math.abs(sum - 1) > 1e-9) {
    throw new Error('startingShares must sum to 1');
  }
  return startingShares;
}

function deriveOutcome(share: number, volumeTotalD6: string): PreviewOutcomeState {
  const volumeNum = Number(volumeTotalD6);
  return {
    share,
    twarValueD9: String(Math.round(share * D9_SCALE)),
    reservesD6: String(Math.round(share * volumeNum)),
  };
}

export function initialState(
  outcomeCount: number,
  startingShares?: ReadonlyArray<number>,
): PreviewSimState {
  const volumeTotalD6 = String(INITIAL_VOLUME_USDC * D6_SCALE);
  const shares = resolveStartingShares(outcomeCount, startingShares);
  return {
    volumeTotalD6,
    prizePoolD6: String(INITIAL_PRIZE_POOL_USDC * D6_SCALE),
    outcomes: shares.map((s) => deriveOutcome(s, volumeTotalD6)),
    liveTrades: [],
  };
}

function pickTradeAmountUsdc(randomValue: number): number {
  // Log-distributed: most trades small, occasional whale. Pick a uniform
  // fraction in [0, 1] then cube it for a long tail — pulls the median down
  // while keeping the max in range, so most trades read as small/medium and
  // the occasional 1000+ USDC clip pumps the volume visibly.
  const u = randomValue;
  const skewed = u * u * u;
  return MIN_TRADE_USDC + skewed * (MAX_TRADE_USDC - MIN_TRADE_USDC);
}

function pickOutcomeIndex(
  outcomes: ReadonlyArray<PreviewOutcomeState>,
  randomValue: number,
): number {
  if (outcomes.length === 0) return 0;
  return Math.floor(randomValue * outcomes.length);
}

function pickSide(randomValue: number): 'buy' | 'sell' {
  return randomValue < 0.62 ? 'buy' : 'sell';
}

function clampAndRenorm(input: ReadonlyArray<number>): ReadonlyArray<number> {
  // Water-filling clamp: clamp to [floor, ceiling], redistribute the resulting
  // surplus/deficit ONLY across outcomes that are not currently pinned to a
  // bound, then loop. This converges without the multiplicative-renorm bug
  // (where dividing by a small total pushes capped values back past the
  // ceiling).
  if (input.length === 0) return input;
  let working: number[] = input.slice();
  for (let iter = 0; iter < 16; iter += 1) {
    const pinned: boolean[] = new Array(working.length).fill(false);
    working = working.map((s, i) => {
      if (s > SHARE_CEILING) {
        pinned[i] = true;
        return SHARE_CEILING;
      }
      if (s < SHARE_FLOOR) {
        pinned[i] = true;
        return SHARE_FLOOR;
      }
      return s;
    });
    const total = working.reduce((acc, n) => acc + n, 0);
    const deficit = 1 - total;
    if (Math.abs(deficit) < 1e-12) return working;
    const unpinnedSum = working.reduce((acc, n, i) => (pinned[i] === true ? acc : acc + n), 0);
    if (unpinnedSum <= 0) {
      // Every outcome is pinned. Spread deficit uniformly as a last resort —
      // can only happen if floor*N > 1 or ceiling*N < 1, neither of which is
      // reachable for the current SHARE_FLOOR / SHARE_CEILING values.
      const fill = 1 / working.length;
      return working.map(() => fill);
    }
    working = working.map((s, i) => {
      if (pinned[i] === true) return s;
      return s + deficit * (s / unpinnedSum);
    });
  }
  return working;
}

function applyShareDrift(
  shares: ReadonlyArray<number>,
  tradedIdx: number,
  side: 'buy' | 'sell',
  randomValue: number,
): ReadonlyArray<number> {
  if (shares.length <= 1) return shares;
  // Bias the drift in the direction of the trade side.
  const direction = side === 'buy' ? 1 : -1;
  const magnitude = SHARE_DRIFT_MIN + randomValue * (SHARE_DRIFT_MAX - SHARE_DRIFT_MIN);
  const delta = magnitude * direction;
  // Apply delta to the traded outcome, redistribute the negative across
  // the others proportionally to their current share.
  const tradedShare = shares[tradedIdx] ?? 0;
  const adjusted = shares.map((s, i) => {
    if (i === tradedIdx) return s + delta;
    const otherTotal = 1 - tradedShare;
    if (otherTotal <= 0) return s;
    return s - delta * (s / otherTotal);
  });
  return clampAndRenorm(adjusted);
}

export interface ApplyTickResult {
  next: PreviewSimState;
  newTrade: LiveTrade;
}

export interface PreviewTickSample {
  readonly amount: number;
  readonly outcome: number;
  readonly side: number;
  readonly drift: number;
  readonly tradeId: string;
}

export function samplePreviewTick(rng: () => number, idFactory: () => string): PreviewTickSample {
  return {
    amount: rng(),
    outcome: rng(),
    side: rng(),
    drift: rng(),
    tradeId: idFactory(),
  };
}

export function applySampledTick(
  state: PreviewSimState,
  sample: PreviewTickSample,
): ApplyTickResult {
  const tradeUsdc = pickTradeAmountUsdc(sample.amount);
  const tradeAmountD6 = Math.floor(tradeUsdc * D6_SCALE);
  const outcomeIndex = pickOutcomeIndex(state.outcomes, sample.outcome);
  const side = pickSide(sample.side);

  const nextVolumeD6 = String(BigInt(state.volumeTotalD6) + BigInt(tradeAmountD6));
  const prizePoolDeltaD6 = Math.floor(tradeAmountD6 * HOUSE_TAKE_RATIO);
  const nextPrizePoolD6 = String(BigInt(state.prizePoolD6) + BigInt(prizePoolDeltaD6));

  const nextShares = applyShareDrift(
    state.outcomes.map((o) => o.share),
    outcomeIndex,
    side,
    sample.drift,
  );
  const nextOutcomes = nextShares.map((s) => deriveOutcome(s, nextVolumeD6));

  const newTrade: LiveTrade = {
    id: sample.tradeId,
    outcomeIndex,
    side,
    amountD6: String(tradeAmountD6),
  };

  return {
    next: {
      volumeTotalD6: nextVolumeD6,
      prizePoolD6: nextPrizePoolD6,
      outcomes: nextOutcomes,
      liveTrades: [...state.liveTrades, newTrade],
    },
    newTrade,
  };
}

export function applyTick(
  state: PreviewSimState,
  rng: () => number,
  idFactory: () => string,
): ApplyTickResult {
  return applySampledTick(state, samplePreviewTick(rng, idFactory));
}

export function expireTrade(state: PreviewSimState, tradeId: string): PreviewSimState {
  return {
    ...state,
    liveTrades: state.liveTrades.filter((t) => t.id !== tradeId),
  };
}

export function nextDelayMs(rng: () => number, scale: number = 1): number {
  return Math.floor((MIN_DELAY_MS + rng() * (MAX_DELAY_MS - MIN_DELAY_MS)) * scale);
}

// Re-exported for tests that want to assert against the constants.
export const PREVIEW_SIM_CONSTANTS = {
  INITIAL_VOLUME_USDC,
  INITIAL_PRIZE_POOL_USDC,
  MIN_DELAY_MS,
  MAX_DELAY_MS,
  MIN_TRADE_USDC,
  MAX_TRADE_USDC,
  SHARE_FLOOR,
  SHARE_CEILING,
} as const;
